import Link from 'next/link';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import styles from './index.module.less';
import type { MarketplaceNavItem } from '@/components/Layouts';
import { NavItem, MobileHeader } from '@/components/Layouts';
import { AppSettings } from '@/shared/app-common';
import Image from 'next/image';
import { Button, Dropdown, MenuProps } from 'antd';
import { useUserProvider } from '@/context/UserProvider';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useConfigProvider } from '@/context/ConfigProvider';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

import dynamic from 'next/dynamic';

const RetroTitle = dynamic(() => import('@/components/Common/RetroTitle'), { ssr: true });
const ChangeLanguage = dynamic(() => import('@/components/Common/Language'), { ssr: false });
const HeaderFeatureContent = dynamic(() => import('../HeaderFeatureContent'), { ssr: false });

interface GlobalHeaderProps {
  navItems?: MarketplaceNavItem[];
  isMobile?: boolean;
  isStarData?: boolean;
}

const GlobalHeader = ({ navItems, isMobile, isStarData = false }: GlobalHeaderProps) => {
  const { isBlogOrSupportWeb } = useConfigProvider();
  const { onOpenLoginModal, onChangedLoginView, isLogin, onSignOut } = useUserProvider();
  const { t } = useTranslation('common');
  const router = useRouter();
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const [featureHover, setFeatureHover] = useState<boolean>(false);
  const [featureDivHover, setFeatureDivHover] = useState<boolean>(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      const promptEvent = deferredPrompt as any;
      promptEvent.prompt();

      // 等待用户回应
      const choiceResult = await promptEvent.userChoice;
      // if (choiceResult.outcome === 'accepted') {
      //   console.log('用户接受了PWA安装');
      // } else {
      //   console.log('用户拒绝了PWA安装');
      // }
      setDeferredPrompt(null); // 安装提示触发后，重置
    }
  };

  const canDisplayUnloginHeader = useMemo(() => {
    return !isLogin && !isBlogOrSupportWeb;
  }, [isLogin, isBlogOrSupportWeb]);

  const UnloginHeader = useMemo(() => {
    return (
      <div
        className={styles.unLogin}
        style={{
          display: canDisplayUnloginHeader ? 'block' : 'none',
        }}
      >
        <span
          onClick={() => {
            onOpenLoginModal(true);
            onChangedLoginView(true);
            // router.push('/dashboard')
          }}
        >
          {t('Join now')}
        </span>
        <span>{t('to learn more about who you care about')}</span>
      </div>
    );
  }, [!isLogin && !router.pathname.includes('instagram-tracking-blog')]);

  const items: MenuProps['items'] = deferredPrompt
    ? [
        {
          key: 'settings',
          label: (
            <Link
              href={'/settings'}
              onClick={() => {
                ga_logEvent(EventName.header_Settings);
              }}
            >
              {t('Settings')}
            </Link>
          ),
        },
        {
          key: 'download-app',
          label: <span onClick={handleInstallClick}>{t('Add to desktop')}</span>,
        },
        {
          key: 'LogOut',
          label: (
            <Link
              href={'/'}
              onClick={() => {
                onSignOut();
                ga_logEvent(EventName.header_Logout);
              }}
            >
              {t('LogOut')}
            </Link>
          ),
        },
      ]
    : [
        {
          key: 'settings',
          label: (
            <Link
              href={'/settings'}
              onClick={() => {
                ga_logEvent(EventName.header_Settings);
              }}
            >
              {t('Settings')}
            </Link>
          ),
        },
        {
          key: 'LogOut',
          label: (
            <Link
              href={'/'}
              onClick={() => {
                onSignOut();
                ga_logEvent(EventName.header_Logout);
              }}
            >
              {t('LogOut')}
            </Link>
          ),
        },
      ];

  const getNavLeftSection = useMemo(() => {
    return (
      <div className={styles.navTitleContainer}>
        <div className={styles.catchPhrase}>
          <Link href={AppSettings.webHost}>
            <RetroTitle
              text={
                <div style={{ height: '32px', display: 'flex', alignItems: 'center' }}>
                  <Image
                    className={styles.logo}
                    src={AppSettings.logoPath}
                    alt={AppSettings.name}
                    loading="lazy"
                    width={32}
                    height={32}
                  />
                  <img
                    src={isStarData ? '/images/layouts/dolphin_radar.webp' : AppSettings.namePath}
                    alt={AppSettings.name}
                    style={{ height: '32px', marginLeft: '10px' }}
                  />
                  {!isStarData && (
                    <Image
                      src={'/images/layouts/collect-icon.webp'}
                      alt="collect-icon.webp"
                      width={32}
                      height={32}
                      style={{ borderRadius: '5px' }}
                    />
                  )}
                </div>
              }
              // textColor="#ff401a"
              fontSize={28}
              showAlert={true}
            />
          </Link>
        </div>
      </div>
    );
  }, [isStarData]);

  const renderNavRightSection = useMemo(() => {
    return navItems?.map((item, index) => (
      <NavItem
        item={item}
        key={index}
        onHover={(isHover?: boolean) => {
          if (item?.gaName === EventName.header_Features) {
            if (isHover) {
              setFeatureHover(isHover);
            } else {
              const timer = setTimeout(() => setFeatureHover(isHover), 300);
              return () => clearTimeout(timer);
            }
          }
        }}
      />
    ));
  }, [isMobile, navItems]);

  if (isMobile) {
    return <MobileHeader>{renderNavRightSection}</MobileHeader>;
  }

  return (
    <div
      className={styles.headerContainer}
      style={{ position: isStarData ? 'absolute' : 'fixed', background: 'transparent' }}
    >
      {UnloginHeader}
      <div
        className={classNames(styles.globalHeaderWarp)}
        style={{ background: isStarData ? 'transparent' : 'var(--foreground-rgb)' }}
      >
        <div className={styles.navSection}>{getNavLeftSection}</div>
        <div className={styles.navSection} style={{ justifyContent: 'center' }}>
          {renderNavRightSection}
        </div>

        <div className={styles.navSection}>
          {isLogin
            ? !isBlogOrSupportWeb && (
                <Dropdown menu={{ items }} placement="bottom" autoAdjustOverflow={false}>
                  <Button
                    className={styles.login}
                    onClick={() => {
                      router.push('/dashboard');
                      ga_logEvent(EventName.header_Dashboard);
                    }}
                  >
                    {t('Dashboard')}
                  </Button>
                </Dropdown>
              )
            : !isBlogOrSupportWeb && (
                <Button
                  className={styles.login}
                  onClick={() => {
                    onOpenLoginModal(true);
                    onChangedLoginView(true);
                    ga_logEvent(EventName.header_SignIn);
                  }}
                >
                  {t('LogIn_SignUp')}
                </Button>
              )}
          <ChangeLanguage />
        </div>
      </div>

      {(featureHover || featureDivHover) && (
        <div
          style={{ width: '100vw', position: 'absolute', top: !canDisplayUnloginHeader ? '64px' : '108px', left: '0' }}
          onMouseEnter={() => {
            setFeatureDivHover(true);
          }}
          onMouseLeave={() => {
            setFeatureDivHover(false);
          }}
        >
          <HeaderFeatureContent />
        </div>
      )}
    </div>
  );
};

export default GlobalHeader;
